import { Link } from "react-router-dom";
import { X, Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";

const formatEUR = (n) => new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(n);

const CartDrawer = () => {
  const { isOpen, setIsOpen, items, subtotal, removeItem, updateQty } = useCart();

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="bg-beige-light border-l border-gold/20 w-full sm:max-w-md flex flex-col p-0"
      >
        <SheetHeader className="p-6 border-b border-gold/15">
          <SheetTitle className="font-serif text-3xl text-ink flex items-center gap-3">
            <ShoppingBag className="w-5 h-5 text-gold" strokeWidth={1.5} />
            Carrinho
          </SheetTitle>
          <SheetDescription className="overline !text-gold">
            {items.length} {items.length === 1 ? "produto" : "produtos"}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center gap-4 py-12">
              <ShoppingBag className="w-12 h-12 text-gold/40" strokeWidth={1} />
              <p className="text-ink/60 font-serif text-2xl">O seu carrinho está vazio</p>
              <Link
                to="/loja"
                onClick={() => setIsOpen(false)}
                data-testid="cart-empty-shop-link"
                className="btn-outline-gold mt-4"
              >
                Explorar Produtos
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-gold/10">
              {items.map((it) => (
                <li key={it.product_id} className="py-5 flex gap-4" data-testid={`cart-item-${it.product_id}`}>
                  <div className="w-20 h-24 bg-beige flex-shrink-0 overflow-hidden">
                    {it.image && (
                      <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 flex flex-col">
                    <p className="overline text-gold">{it.line}</p>
                    <p className="font-serif text-lg text-ink leading-snug">{it.name}</p>
                    <p className="text-xs text-ink/50">{it.size}</p>
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center border border-gold/30">
                        <button
                          data-testid={`cart-decr-${it.product_id}`}
                          onClick={() => updateQty(it.product_id, it.quantity - 1)}
                          className="p-2 hover:bg-gold/10 transition-colors"
                          aria-label="Diminuir"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="px-3 text-sm font-medium" data-testid={`cart-qty-${it.product_id}`}>
                          {it.quantity}
                        </span>
                        <button
                          data-testid={`cart-incr-${it.product_id}`}
                          onClick={() => updateQty(it.product_id, it.quantity + 1)}
                          className="p-2 hover:bg-gold/10 transition-colors"
                          aria-label="Aumentar"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <span className="text-gold font-medium" data-testid={`cart-line-total-${it.product_id}`}>
                        {formatEUR(it.price * it.quantity)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => removeItem(it.product_id)}
                    data-testid={`cart-remove-${it.product_id}`}
                    className="text-ink/40 hover:text-gold transition-colors self-start"
                    aria-label="Remover"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-gold/15 p-6 space-y-4 bg-beige">
            <div className="flex justify-between items-center">
              <span className="overline">Subtotal</span>
              <span className="font-serif text-2xl text-ink" data-testid="cart-subtotal">
                {formatEUR(subtotal)}
              </span>
            </div>
            <p className="text-xs text-ink/50">Portes calculados no checkout.</p>
            <Link
              to="/carrinho"
              onClick={() => setIsOpen(false)}
              data-testid="cart-checkout-link"
              className="btn-gold w-full"
            >
              Finalizar Encomenda
            </Link>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
